import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { RegionFeatureOverrideAbility } from '../../shared/region-feature-override-ability.model';
import { Option } from '../../shared/feature-utils';
import { CarrierService } from '../../shared/carrier.service';
import { RegionFeatureOverrideable } from '../../shared/region-features.model';
import { ModalService } from '../../../shared/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { SwitcherComponent } from '../../../shared/switcher/switcher.component';
import { Carrier } from '../../shared/carrier.model';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { InheritableOnOffFeature, OnOffFeature } from '../../shared/on-off-feature.model';

export enum RegionFeatureDisplayMode {
  /* Component value is of boolean type */
  BOOLEAN_MODE = 'BOOLEAN_MODE',
  /* Component value is of type FeatureUtils.ON_OFF_POSSIBLE_VALUES */
  ON_OFF_FEATURE_MODE = 'ON_OFF_FEATURE_MODE',
  /* Component value is of type FeatureUtils.INHERITABLE_ON_OFF_POSSIBLE_VALUES  */
  INHERITED_ON_OFF_FEATURE_MODE = 'INHERITED_ON_OFF_FEATURE_MODE',
  /* Component value is one of the provided possibleValues */
  MULTI_SELECTION_MODE = 'MULTI_SELECTION_MODE'
}


@Component({
  selector: 'app-region-feature-new',
  templateUrl: './region-feature-new.component.html',
  styleUrls: ['./region-feature-new.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class RegionFeatureNewComponent implements OnInit, OnChanges {

  @Input() displayMode: RegionFeatureDisplayMode;
  @Input() regionId: string;
  @Input() translationNode: string;
  @Input() hideSeparator: boolean;
  @Input() padding = true;
  @Input() displayMoreDescription: boolean;
  @Input() groupName: string;
  @Input() overrideAbility: RegionFeatureOverrideAbility;
  @Input() regionFeature: RegionFeatureOverrideable;
  @Input() value: any;
  @Input() onOffValue: boolean;
  @Input() inheritedOnOffValue: boolean;
  @Output() valueChange = new EventEmitter<any>();
  @Input() possibleValues: Option[] = [];
  @Input() disabled = false;
  @Input() clearable = true;
  @Input() showLabel = true;
  @Input() selectDescription = false;

  @Output() overrideAbilityChange = new EventEmitter<any>();

  @ViewChild('overrideWarning') warningContent: TemplateRef<any>;
  @ViewChild('switcher') switcher: SwitcherComponent;

  overridable = true;

  warningCarriers: Carrier[];
  warningCount: number;

  public readonly RegionFeatureDisplayMode = RegionFeatureDisplayMode;

  constructor(
    private carrierService: CarrierService,
    private modalService: ModalService,
    private translation: TranslateService,
    private errorHandler: ErrorHandlerService,
    ) {
  }

  ngOnInit() {
    if (this.disabled) {
      this.overridable = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value && changes.value.currentValue) {
      if (this.displayMode === RegionFeatureDisplayMode.INHERITED_ON_OFF_FEATURE_MODE) {
        this.inheritedOnOffValue = changes.value.currentValue === InheritableOnOffFeature.ON;
      }
      if (this.displayMode === RegionFeatureDisplayMode.ON_OFF_FEATURE_MODE) {
        this.onOffValue = changes.value.currentValue === OnOffFeature.ON;
      }
    }
    if (changes.overrideAbility && changes.overrideAbility.currentValue) {
      switch (changes.overrideAbility.currentValue as RegionFeatureOverrideAbility) {
        case RegionFeatureOverrideAbility.OVERRIDABLE:
          this.overridable = true;
          break;
        case RegionFeatureOverrideAbility.NOT_OVERRIDABLE:
          this.overridable = false;
          break;
      }
    }
    if (changes.overrideAbility && changes.overrideAbility.currentValue) {
      switch (changes.overrideAbility.currentValue as RegionFeatureOverrideAbility) {
        case RegionFeatureOverrideAbility.OVERRIDABLE:
          this.overridable = true;
          break;
        case RegionFeatureOverrideAbility.NOT_OVERRIDABLE:
          this.overridable = false;
          break;
      }
    }
    if (changes.disabled && null != changes.disabled.currentValue) {
      this.overridable = !changes.disabled.currentValue;
    }
  }

  overridableChanged() {
    if (this.overridable) {
      this.setOverrideAbility(RegionFeatureOverrideAbility.OVERRIDABLE);
    } else {
      this.carrierService.getCarrierCountHavingRegionFeatureOverride(this.regionId, this.regionFeature)
        .subscribe({
          next : carriers => {
            this.warningCarriers = carriers;
            this.warningCount = carriers.length;
            if (this.warningCount === 0) {
              this.setOverrideAbility(RegionFeatureOverrideAbility.NOT_OVERRIDABLE);
              return;
            }
            this.modalService.open({
              titleKey: 'regions.form.features.override-ability.warning.title',
              titleParam: { count: this.warningCount },
              content: this.warningContent,
              closeButton: {
                textKey: 'regions.form.features.override-ability.warning.cancel',
                action: () => this.switcher.value = true
              },
              validateButton: {
                textKey: 'regions.form.features.override-ability.warning.validate',
                action: () => {
                  this.setOverrideAbility(RegionFeatureOverrideAbility.NOT_OVERRIDABLE);
                }
              }
            }).subscribe();
          }, error : (e) => {
            this.switcher.value = true;
            this.errorHandler.handleHttpError(e, 'regions.form.errors.carrier-count');
          }
        });
    }
  }

  onOffValueChanged() {
      this.valueChange.emit(this.onOffValue ? OnOffFeature.ON : OnOffFeature.OFF);
  }

  inheritedOnOffValueChanged() {
    this.valueChange.emit(this.inheritedOnOffValue ? InheritableOnOffFeature.ON : InheritableOnOffFeature.OFF);
  }

  valueChanged() {
    this.valueChange.emit(this.value);
  }

  getFlagLabel() {
    return this.translation.instant(this.translationNode + '.label');
  }

  private setOverrideAbility(value: RegionFeatureOverrideAbility) {
    this.overrideAbility = value;
    this.overrideAbilityChange.emit(this.overrideAbility);
  }
}
